import React from "react";
import {
  Briefcase,
  Building,
  UserCheck,
  Calendar,
  Layers,
  ShieldCheck,
} from "lucide-react";
import {
  StaffDetails,
  StaffDepartment,
  StaffDepartmentLabels,
  EmploymentType,
  EmploymentTypeLabels,
  StaffOption,
} from "../../../types/staff.types";
import { StaffStatusBadge } from "../StaffStatusBadge";

interface EmploymentTabProps {
  details: StaffDetails | null;
  isEditing: boolean;
  editFormData: any;
  managerOptions: StaffOption[];
  staffId: string;
  onChange: (field: string, value: any) => void;
}

export const EmploymentTab: React.FC<EmploymentTabProps> = ({
  details,
  isEditing,
  editFormData,
  managerOptions,
  staffId,
  onChange,
}) => {
  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 border-b border-gray-200/80 pb-2.5">
            <Briefcase className="w-4 h-4 text-emerald-600" />
            <span>Role & Department Assignment</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                value={editFormData.department || ""}
                onChange={(e) => onChange("department", e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              >
                {Object.values(StaffDepartment).map((d) => (
                  <option key={d} value={d}>
                    {StaffDepartmentLabels[d]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Designation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editFormData.designation || ""}
                onChange={(e) => onChange("designation", e.target.value)}
                placeholder="e.g. Senior Pharmacist"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Employment Type
              </label>
              <select
                value={editFormData.employmentType || ""}
                onChange={(e) => onChange("employmentType", e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              >
                {Object.values(EmploymentType).map((t) => (
                  <option key={t} value={t}>
                    {EmploymentTypeLabels[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Joining Date
              </label>
              <input
                type="date"
                value={editFormData.joiningDate ? editFormData.joiningDate.split("T")[0] : ""}
                onChange={(e) => onChange("joiningDate", e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Reporting Manager
              </label>
              <select
                value={editFormData.reportingManagerId || ""}
                onChange={(e) => onChange("reportingManagerId", e.target.value)}
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
              >
                <option value="">None / Direct Clinic Head</option>
                {managerOptions
                  .filter((m) => m.id !== staffId)
                  .map((mgr) => (
                    <option key={mgr.id} value={mgr.id}>
                      {mgr.name} ({mgr.designation})
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Card 1: Position & Department */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-sm font-bold text-gray-900">
          <Briefcase className="w-4 h-4 text-emerald-600" />
          <span>Position & Department</span>
        </div>

        <div className="grid grid-cols-2 gap-y-3.5 text-xs">
          <div>
            <p className="text-gray-400 font-medium">Department</p>
            <p className="text-gray-900 font-semibold text-sm mt-0.5">
              {StaffDepartmentLabels[details?.department as StaffDepartment] || details?.department || "General"}
            </p>
          </div>
          <div>
            <p className="text-gray-400 font-medium">Designation</p>
            <p className="text-gray-800 font-medium mt-0.5">{details?.designation || "N/A"}</p>
          </div>
          <div>
            <p className="text-gray-400 font-medium">Employment Type</p>
            <p className="text-gray-800 font-medium mt-0.5">
              {EmploymentTypeLabels[details?.employmentType as EmploymentType] || details?.employmentType || "Permanent"}
            </p>
          </div>
          <div>
            <p className="text-gray-400 font-medium">Joining Date</p>
            <p className="text-gray-800 font-medium mt-0.5">
              {details?.joiningDate ? new Date(details.joiningDate).toLocaleDateString("en-IN") : "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Card 2: Hierarchy & Status */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-sm font-bold text-gray-900">
          <UserCheck className="w-4 h-4 text-emerald-600" />
          <span>Hierarchy & Employment Status</span>
        </div>

        <div className="grid grid-cols-2 gap-y-3.5 text-xs">
          <div className="col-span-2">
            <p className="text-gray-400 font-medium">Reporting Manager</p>
            <p className="text-gray-900 font-semibold text-sm mt-0.5">
              {details?.reportingManagerName || "Direct Report (Clinic Operations Head)"}
            </p>
          </div>
          <div>
            <p className="text-gray-400 font-medium">Employee Status</p>
            <div className="mt-1">
              <StaffStatusBadge status={details?.employeeStatus || "Active"} />
            </div>
          </div>
          <div>
            <p className="text-gray-400 font-medium">Employee Code</p>
            <p className="text-gray-800 font-mono font-medium mt-0.5">
              {details?.employeeCode || "Code N/A"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
